// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function formatNaira(amount) {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}
function formatNumber(num) {
  return new Intl.NumberFormat("en-NG").format(num);
}
function formatPercent(value, decimals = 1) {
  return `${value.toFixed(decimals)}%`;
}
function calculateProgress(sold, total) {
  if (total === 0) return 0;
  return Math.min(sold / total * 100, 100);
}
function formatDate(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric"
  }).format(d);
}
function formatCountdown(targetDate) {
  const target = typeof targetDate === "string" ? new Date(targetDate) : targetDate;
  const now = /* @__PURE__ */ new Date();
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }
  const days = Math.floor(diff / (1e3 * 60 * 60 * 24));
  const hours = Math.floor(diff % (1e3 * 60 * 60 * 24) / (1e3 * 60 * 60));
  const minutes = Math.floor(diff % (1e3 * 60 * 60) / (1e3 * 60));
  const seconds = Math.floor(diff % (1e3 * 60) / 1e3);
  return { days, hours, minutes, seconds, isExpired: false };
}
function padZero(num) {
  return num.toString().padStart(2, "0");
}
function formatTicketNumber(num) {
  return `#${num.toString().padStart(6, "0")}`;
}
function truncate(str, length) {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}
function isTouchDevice() {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}
function triggerHaptic(duration = 5) {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    navigator.vibrate(duration);
  }
}
function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 640;
}
function isDesktopViewport() {
  if (typeof window === "undefined") return true;
  return window.innerWidth >= 1024;
}
var vaultSpring = {
  damping: 30,
  stiffness: 400,
  mass: 1
};
var vaultLockSpring = {
  damping: 40,
  stiffness: 500,
  mass: 1.2
};
var hoverSpring = {
  damping: 20,
  stiffness: 300,
  mass: 0.8
};
var staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
var fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
};
function formatTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-NG", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).format(d);
}

// src/hooks/index.ts
import { useEffect, useState, useCallback, useRef } from "react";
function useViewport() {
  const [viewport, setViewport] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768
  });
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setViewport({
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024,
        width,
        height
      });
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return viewport;
}
function useMousePosition() {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [normalizedPosition, setNormalizedPosition] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
      setNormalizedPosition({
        x: e.clientX / window.innerWidth * 2 - 1,
        y: e.clientY / window.innerHeight * 2 - 1
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  return { position, normalizedPosition };
}
function useHoverPosition() {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [relativePosition, setRelativePosition] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const handleMouseMove = (e) => {
      const rect = element.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width * 2 - 1;
      const y = (e.clientY - rect.top) / rect.height * 2 - 1;
      setRelativePosition({ x, y });
    };
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => {
      setIsHovered(false);
      setRelativePosition({ x: 0, y: 0 });
    };
    element.addEventListener("mousemove", handleMouseMove);
    element.addEventListener("mouseenter", handleMouseEnter);
    element.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      element.removeEventListener("mousemove", handleMouseMove);
      element.removeEventListener("mouseenter", handleMouseEnter);
      element.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);
  return { ref, isHovered, relativePosition };
}
function useCountdown(targetDate) {
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false
  });
  useEffect(() => {
    const target = typeof targetDate === "string" ? new Date(targetDate) : targetDate;
    const calculateCountdown = () => {
      const now = /* @__PURE__ */ new Date();
      const diff = target.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1e3 * 60 * 60 * 24)),
        hours: Math.floor(diff % (1e3 * 60 * 60 * 24) / (1e3 * 60 * 60)),
        minutes: Math.floor(diff % (1e3 * 60 * 60) / (1e3 * 60)),
        seconds: Math.floor(diff % (1e3 * 60) / 1e3),
        isExpired: false
      });
    };
    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1e3);
    return () => clearInterval(interval);
  }, [targetDate]);
  return countdown;
}
function useInView(options) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [hasBeenInView, setHasBeenInView] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
      if (entry.isIntersecting) {
        setHasBeenInView(true);
      }
    }, options);
    observer.observe(element);
    return () => observer.disconnect();
  }, [options]);
  return { ref, isInView, hasBeenInView };
}
function useScrollPosition() {
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [scrollDirection, setScrollDirection] = useState(null);
  const lastScrollY = useRef(0);
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollPosition({ x: window.scrollX, y: currentScrollY });
      if (currentScrollY > lastScrollY.current) {
        setScrollDirection("down");
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection("up");
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return { scrollPosition, scrollDirection };
}
function useSlideToConfirm(onConfirm, threshold = 0.85) {
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);
  const handleDragMove = useCallback((clientX) => {
    if (!isDragging || !trackRef.current || isConfirmed) return;
    const track = trackRef.current;
    const rect = track.getBoundingClientRect();
    const thumbWidth = 48;
    const maxX = rect.width - thumbWidth - 8;
    const currentX = Math.max(0, Math.min(clientX - rect.left - thumbWidth / 2, maxX));
    const newProgress = currentX / maxX;
    setProgress(newProgress);
    if (newProgress >= threshold) {
      setIsConfirmed(true);
      setIsDragging(false);
      onConfirm();
    }
  }, [isDragging, isConfirmed, onConfirm, threshold]);
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    if (!isConfirmed) {
      setProgress(0);
    }
  }, [isConfirmed]);
  const reset = useCallback(() => {
    setProgress(0);
    setIsConfirmed(false);
    setIsDragging(false);
  }, []);
  return {
    trackRef,
    progress,
    isDragging,
    isConfirmed,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    reset
  };
}
function useHaptic() {
  const triggerHaptic2 = useCallback((duration = 5) => {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(duration);
    }
  }, []);
  const triggerSuccessHaptic = useCallback(() => {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate([10, 50, 10]);
    }
  }, []);
  const triggerErrorHaptic = useCallback(() => {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate([50, 50, 50]);
    }
  }, []);
  return { triggerHaptic: triggerHaptic2, triggerSuccessHaptic, triggerErrorHaptic };
}
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });
  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);
  return [storedValue, setValue];
}

// src/components/Button.tsx
import { forwardRef } from "react";
import { motion } from "framer-motion";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = cva(
  "relative inline-flex items-center justify-center font-semibold transition-all duration-vault ease-vault focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        gold: "btn-gold",
        outline: "border border-gold-500/50 text-gold-500 hover:bg-gold-500/10 hover:border-gold-500",
        ghost: "btn-ghost",
        danger: "bg-vault-error/20 text-vault-error border border-vault-error/30 hover:bg-vault-error/30",
        success: "bg-vault-success/20 text-vault-success border border-vault-success/30 hover:bg-vault-success/30"
      },
      size: {
        sm: "h-9 px-4 text-sm rounded-lg",
        md: "h-11 px-6 text-sm rounded-vault",
        lg: "h-13 px-8 text-base rounded-vault-lg",
        xl: "h-14 px-10 text-lg rounded-vault-lg",
        icon: "h-10 w-10 rounded-full"
      },
      fullWidth: {
        true: "w-full",
        false: ""
      }
    },
    defaultVariants: {
      variant: "gold",
      size: "md",
      fullWidth: false
    }
  }
);
var Button = forwardRef(
  ({ className, variant, size, fullWidth, loading, haptic = true, children, onClick, ...props }, ref) => {
    const handleClick = (e) => {
      if (haptic) triggerHaptic(5);
      onClick?.(e);
    };
    return /* @__PURE__ */ jsx(
      motion.button,
      {
        ref,
        className: cn(buttonVariants({ variant, size, fullWidth, className })),
        whileHover: { scale: 1.02 },
        whileTap: { scale: 0.98 },
        transition: vaultSpring,
        onClick: handleClick,
        disabled: loading,
        ...props,
        children: loading ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "h-4 w-4 animate-spin",
              viewBox: "0 0 24 24",
              fill: "none",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          "Processing..."
        ] }) : children
      }
    );
  }
);
Button.displayName = "Button";
var ShimmerButton = forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      Button,
      {
        ref,
        className: cn(
          "overflow-hidden",
          "before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
          "before:animate-shimmer before:bg-[length:200%_100%]",
          className
        ),
        ...props,
        children
      }
    );
  }
);
ShimmerButton.displayName = "ShimmerButton";

// src/components/Card.tsx
import { forwardRef as forwardRef2 } from "react";
import { motion as motion2 } from "framer-motion";
import { jsx as jsx2 } from "react/jsx-runtime";
var Card = forwardRef2(
  ({ className, variant = "default", hover = false, glow = false, children, ...props }, ref) => {
    const variants = {
      default: "vault-card",
      elevated: "bg-surface-2 border border-white/10 rounded-vault-lg shadow-vault-lg",
      glass: "bg-surface-1/60 backdrop-blur-glass border border-white/10 rounded-vault",
      interactive: "vault-card cursor-pointer"
    };
    const baseClasses = cn(
      variants[variant],
      hover && "jet-glide md:hover:scale-[1.02] md:hover:shadow-vault-lg",
      glow && "hover:shadow-vault-glow",
      "touch-active",
      className
    );
    if (hover || variant === "interactive") {
      return /* @__PURE__ */ jsx2(
        motion2.div,
        {
          ref,
          className: baseClasses,
          whileHover: { scale: 1.02 },
          whileTap: { scale: 0.98 },
          transition: vaultSpring,
          ...props,
          children
        }
      );
    }
    return /* @__PURE__ */ jsx2("div", { ref, className: baseClasses, ...props, children });
  }
);
Card.displayName = "Card";
var CardHeader = forwardRef2(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
    "div",
    {
      ref,
      className: cn("flex flex-col space-y-1.5 p-6", className),
      ...props
    }
  )
);
CardHeader.displayName = "CardHeader";
var CardTitle = forwardRef2(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
    "h3",
    {
      ref,
      className: cn("font-display text-xl font-semibold text-text-primary", className),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = forwardRef2(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
    "p",
    {
      ref,
      className: cn("text-sm text-text-secondary", className),
      ...props
    }
  )
);
CardDescription.displayName = "CardDescription";
var CardContent = forwardRef2(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx2("div", { ref, className: cn("p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = forwardRef2(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
    "div",
    {
      ref,
      className: cn("flex items-center p-6 pt-0", className),
      ...props
    }
  )
);
CardFooter.displayName = "CardFooter";

// src/components/Dialog.tsx
import React3, { forwardRef as forwardRef3 } from "react";
import { motion as motion3, AnimatePresence, useDragControls } from "framer-motion";
import { X } from "lucide-react";
import { Fragment, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var BottomSheet = forwardRef3(
  ({ isOpen, onClose, children, title, snapPoints = [0.5, 0.9], initialSnap = 0, className }, ref) => {
    const dragControls = useDragControls();
    const [currentSnap, setCurrentSnap] = React3.useState(initialSnap);
    const handleDragEnd = (_event, info) => {
      const velocity = info.velocity.y;
      const offset = info.offset.y;
      if (velocity > 500 || offset > 200) {
        onClose();
        return;
      }
      const viewportHeight = window.innerHeight;
      const currentY = viewportHeight * (1 - snapPoints[currentSnap]) + offset;
      const currentPercent = 1 - currentY / viewportHeight;
      let closestSnap = 0;
      let closestDistance = Infinity;
      snapPoints.forEach((point, index) => {
        const distance = Math.abs(point - currentPercent);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestSnap = index;
        }
      });
      setCurrentSnap(closestSnap);
    };
    return /* @__PURE__ */ jsx3(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs2(Fragment, { children: [
      /* @__PURE__ */ jsx3(
        motion3.div,
        {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: { duration: 0.2 },
          className: "fixed inset-0 z-bottom-sheet bg-black/60 backdrop-blur-sm",
          onClick: onClose
        }
      ),
      /* @__PURE__ */ jsxs2(
        motion3.div,
        {
          ref,
          initial: { y: "100%" },
          animate: { y: `${(1 - snapPoints[currentSnap]) * 100}%` },
          exit: { y: "100%" },
          transition: vaultSpring,
          drag: "y",
          dragControls,
          dragConstraints: { top: 0 },
          dragElastic: 0.1,
          onDragEnd: handleDragEnd,
          className: cn(
            "fixed inset-x-0 bottom-0 z-bottom-sheet",
            "bg-surface-1 rounded-t-vault-xl",
            "border-t border-white/10",
            "shadow-vault-lg",
            "max-h-[90vh]",
            className
          ),
          children: [
            /* @__PURE__ */ jsx3(
              "div",
              {
                className: "flex justify-center pt-4 pb-2 cursor-grab active:cursor-grabbing",
                onPointerDown: (e) => dragControls.start(e),
                children: /* @__PURE__ */ jsx3("div", { className: "sheet-handle" })
              }
            ),
            title && /* @__PURE__ */ jsxs2("div", { className: "flex items-center justify-between px-6 pb-4 border-b border-white/5", children: [
              /* @__PURE__ */ jsx3("h2", { className: "font-display text-lg font-semibold text-text-primary", children: title }),
              /* @__PURE__ */ jsx3(
                "button",
                {
                  onClick: onClose,
                  className: "p-2 rounded-full hover:bg-white/5 transition-colors",
                  children: /* @__PURE__ */ jsx3(X, { className: "w-5 h-5 text-text-secondary" })
                }
              )
            ] }),
            /* @__PURE__ */ jsx3("div", { className: "overflow-y-auto max-h-[calc(90vh-100px)] overscroll-contain", children })
          ]
        }
      )
    ] }) });
  }
);
BottomSheet.displayName = "BottomSheet";
var Modal = forwardRef3(
  ({ isOpen, onClose, children, title, size = "md", className }, ref) => {
    const sizeClasses = {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      full: "max-w-4xl"
    };
    return /* @__PURE__ */ jsx3(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs2(Fragment, { children: [
      /* @__PURE__ */ jsx3(
        motion3.div,
        {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: { duration: 0.2 },
          className: "fixed inset-0 z-modal bg-black/70 backdrop-blur-sm",
          onClick: onClose
        }
      ),
      /* @__PURE__ */ jsx3("div", { className: "fixed inset-0 z-modal flex items-center justify-center p-4", children: /* @__PURE__ */ jsxs2(
        motion3.div,
        {
          ref,
          initial: { scale: 0.95, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0.95, opacity: 0 },
          transition: vaultSpring,
          className: cn(
            "w-full",
            sizeClasses[size],
            "bg-surface-1 rounded-vault-xl",
            "border border-white/10",
            "shadow-vault-lg",
            "overflow-hidden",
            className
          ),
          onClick: (e) => e.stopPropagation(),
          children: [
            title && /* @__PURE__ */ jsxs2("div", { className: "flex items-center justify-between px-6 py-4 border-b border-white/5", children: [
              /* @__PURE__ */ jsx3("h2", { className: "font-display text-xl font-semibold text-text-primary", children: title }),
              /* @__PURE__ */ jsx3(
                "button",
                {
                  onClick: onClose,
                  className: "p-2 rounded-full hover:bg-white/5 transition-colors",
                  children: /* @__PURE__ */ jsx3(X, { className: "w-5 h-5 text-text-secondary" })
                }
              )
            ] }),
            /* @__PURE__ */ jsx3("div", { className: "max-h-[70vh] overflow-y-auto", children })
          ]
        }
      ) })
    ] }) });
  }
);
Modal.displayName = "Modal";
var ResponsiveDialog = ({
  isOpen,
  onClose,
  children,
  title,
  sheetSnapPoints = [0.5, 0.9],
  modalSize = "md",
  className
}) => {
  const [isMobile, setIsMobile] = React3.useState(false);
  React3.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  if (isMobile) {
    return /* @__PURE__ */ jsx3(
      BottomSheet,
      {
        isOpen,
        onClose,
        title,
        snapPoints: sheetSnapPoints,
        className,
        children
      }
    );
  }
  return /* @__PURE__ */ jsx3(
    Modal,
    {
      isOpen,
      onClose,
      title,
      size: modalSize,
      className,
      children
    }
  );
};

// src/components/SlideToConfirm.tsx
import React4 from "react";
import { motion as motion4 } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var SlideToConfirm = ({
  onConfirm,
  label = "Slide to Confirm",
  confirmLabel = "Confirmed!",
  disabled = false,
  loading = false,
  className
}) => {
  const {
    trackRef,
    progress,
    isDragging,
    isConfirmed,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    reset
  } = useSlideToConfirm(() => {
    triggerHaptic(10);
    onConfirm();
  });
  const handleTouchMove = (e) => {
    if (disabled || loading) return;
    handleDragMove(e.touches[0].clientX);
  };
  const handleMouseMove = (e) => {
    if (disabled || loading || !isDragging) return;
    handleDragMove(e.clientX);
  };
  React4.useEffect(() => {
    if (loading) return;
    const handleGlobalMouseUp = () => handleDragEnd();
    const handleGlobalTouchEnd = () => handleDragEnd();
    if (isDragging) {
      window.addEventListener("mouseup", handleGlobalMouseUp);
      window.addEventListener("touchend", handleGlobalTouchEnd);
    }
    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
      window.removeEventListener("touchend", handleGlobalTouchEnd);
    };
  }, [isDragging, handleDragEnd, loading]);
  React4.useEffect(() => {
    if (isConfirmed && !loading) {
      const timer = setTimeout(reset, 2e3);
      return () => clearTimeout(timer);
    }
  }, [isConfirmed, loading, reset]);
  const thumbX = progress * (trackRef.current?.offsetWidth || 0 - 56);
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      ref: trackRef,
      className: cn(
        "slide-track relative select-none",
        disabled && "opacity-50 cursor-not-allowed",
        isConfirmed && "bg-vault-success/20 border-vault-success/30",
        className
      ),
      onMouseMove: handleMouseMove,
      onTouchMove: handleTouchMove,
      children: [
        /* @__PURE__ */ jsx4(
          motion4.div,
          {
            className: cn(
              "absolute inset-y-0 left-0 rounded-full",
              isConfirmed ? "bg-vault-success/20" : "bg-gold-500/10"
            ),
            style: { width: `${progress * 100}%` },
            transition: vaultSpring
          }
        ),
        /* @__PURE__ */ jsx4("div", { className: "absolute inset-0 flex items-center justify-center pointer-events-none", children: /* @__PURE__ */ jsx4(
          motion4.span,
          {
            className: cn(
              "text-sm font-medium",
              isConfirmed ? "text-vault-success" : "text-text-secondary"
            ),
            initial: { opacity: 1 },
            animate: { opacity: isConfirmed ? 1 : 1 - progress * 2 },
            children: loading ? /* @__PURE__ */ jsxs3("span", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsxs3("svg", { className: "w-4 h-4 animate-spin", viewBox: "0 0 24 24", fill: "none", children: [
                /* @__PURE__ */ jsx4("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
                /* @__PURE__ */ jsx4("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })
              ] }),
              "Processing..."
            ] }) : isConfirmed ? confirmLabel : label
          }
        ) }),
        !isConfirmed && !loading && /* @__PURE__ */ jsxs3(
          motion4.div,
          {
            className: cn(
              "slide-thumb",
              isDragging && "cursor-grabbing"
            ),
            style: { x: thumbX },
            animate: { x: isDragging ? thumbX : progress * ((trackRef.current?.offsetWidth || 200) - 56) },
            transition: isDragging ? { duration: 0 } : vaultSpring,
            onMouseDown: () => !disabled && handleDragStart(),
            onTouchStart: () => !disabled && handleDragStart(),
            children: [
              /* @__PURE__ */ jsx4(ChevronRight, { className: "w-5 h-5" }),
              /* @__PURE__ */ jsx4(ChevronRight, { className: "w-5 h-5 -ml-3 opacity-50" })
            ]
          }
        ),
        isConfirmed && !loading && /* @__PURE__ */ jsx4(
          motion4.div,
          {
            className: "absolute left-1 top-1 bottom-1 w-12 bg-vault-success rounded-full flex items-center justify-center",
            initial: { scale: 0 },
            animate: { scale: 1 },
            transition: vaultSpring,
            children: /* @__PURE__ */ jsx4("svg", { className: "w-6 h-6 text-white", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ jsx4("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 3, d: "M5 13l4 4L19 7" }) })
          }
        )
      ]
    }
  );
};

// src/components/TicketCard.tsx
import { useEffect as useEffect2 } from "react";
import { motion as motion5, useMotionValue, useSpring } from "framer-motion";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var TicketCard = ({
  ticket,
  onClick,
  className
}) => {
  const { isDesktop } = useViewport();
  const { ref, isHovered, relativePosition } = useHoverPosition();
  const rotateX = useSpring(useMotionValue(0), vaultSpring);
  const rotateY = useSpring(useMotionValue(0), vaultSpring);
  useEffect2(() => {
    if (isDesktop && isHovered) {
      rotateX.set(relativePosition.y * -10);
      rotateY.set(relativePosition.x * 10);
    } else {
      rotateX.set(0);
      rotateY.set(0);
    }
  }, [isDesktop, isHovered, relativePosition, rotateX, rotateY]);
  const statusColors = {
    active: "border-gold-500/50 bg-gold-500/10",
    drawn: "border-text-muted/30 bg-text-muted/10",
    won: "border-vault-success/50 bg-vault-success/10",
    lost: "border-vault-error/30 bg-vault-error/10"
  };
  const statusLabels = {
    active: "Active",
    drawn: "Drawn",
    won: "Winner!",
    lost: "Not Selected"
  };
  if (isDesktop) {
    return /* @__PURE__ */ jsx5(
      motion5.div,
      {
        ref,
        className: cn(
          "relative cursor-pointer perspective-1000",
          className
        ),
        style: {
          rotateX,
          rotateY,
          transformStyle: "preserve-3d"
        },
        whileHover: { scale: 1.02, z: 20 },
        whileTap: { scale: 0.98 },
        onClick,
        children: /* @__PURE__ */ jsxs4("div", { className: cn(
          "relative overflow-hidden rounded-vault-lg border",
          "bg-gradient-to-br from-surface-1 via-surface-2 to-surface-1",
          statusColors[ticket.status]
        ), children: [
          /* @__PURE__ */ jsx5(
            "div",
            {
              className: "absolute inset-0 foil-effect opacity-30 pointer-events-none",
              style: {
                backgroundPosition: `${50 + relativePosition.x * 50}% ${50 + relativePosition.y * 50}%`
              }
            }
          ),
          /* @__PURE__ */ jsxs4("div", { className: "relative p-6", children: [
            /* @__PURE__ */ jsxs4("div", { className: "flex items-start justify-between mb-4", children: [
              /* @__PURE__ */ jsxs4("div", { children: [
                /* @__PURE__ */ jsx5("p", { className: "text-xs text-text-muted uppercase tracking-wider mb-1", children: "Ticket" }),
                /* @__PURE__ */ jsx5("p", { className: "font-mono text-2xl font-bold text-gold-500 tabular-nums", children: formatTicketNumber(ticket.ticketNumber) })
              ] }),
              /* @__PURE__ */ jsx5("span", { className: cn(
                "status-badge",
                ticket.status === "active" && "status-active",
                ticket.status === "drawn" && "status-closed",
                ticket.status === "won" && "status-drawn",
                ticket.status === "lost" && "bg-vault-error/20 text-vault-error border-vault-error/30"
              ), children: statusLabels[ticket.status] })
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "flex gap-4 mb-4", children: [
              ticket.propertyImage && /* @__PURE__ */ jsx5("div", { className: "w-20 h-14 rounded-lg overflow-hidden bg-surface-2 flex-shrink-0", children: /* @__PURE__ */ jsx5(
                "img",
                {
                  src: ticket.propertyImage,
                  alt: "",
                  className: "w-full h-full object-cover"
                }
              ) }),
              /* @__PURE__ */ jsxs4("div", { className: "flex-1 min-w-0", children: [
                /* @__PURE__ */ jsx5("p", { className: "font-display text-sm font-medium text-text-primary truncate", children: ticket.propertyTitle }),
                /* @__PURE__ */ jsxs4("p", { className: "text-xs text-text-muted mt-1", children: [
                  "Purchased ",
                  new Date(ticket.purchaseDate).toLocaleDateString()
                ] })
              ] })
            ] }),
            /* @__PURE__ */ jsxs4("div", { className: "flex items-center justify-between pt-4 border-t border-white/5", children: [
              /* @__PURE__ */ jsxs4("div", { children: [
                /* @__PURE__ */ jsx5("p", { className: "text-xs text-text-muted", children: "Ticket Price" }),
                /* @__PURE__ */ jsx5("p", { className: "font-mono text-lg font-semibold text-text-primary tabular-nums", children: formatNaira(ticket.price) })
              ] }),
              /* @__PURE__ */ jsx5("div", { className: "w-8 h-8 rounded-full bg-gold-500/20 flex items-center justify-center", children: /* @__PURE__ */ jsx5("svg", { className: "w-4 h-4 text-gold-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ jsx5("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" }) }) })
            ] })
          ] }),
          /* @__PURE__ */ jsx5("div", { className: "absolute left-0 top-1/2 -translate-y-1/2 w-4 h-8 bg-surface-0 rounded-r-full" }),
          /* @__PURE__ */ jsx5("div", { className: "absolute right-0 top-1/2 -translate-y-1/2 w-4 h-8 bg-surface-0 rounded-l-full" })
        ] })
      }
    );
  }
  return /* @__PURE__ */ jsx5(
    motion5.div,
    {
      className: cn(
        "relative overflow-hidden rounded-vault border p-4",
        "bg-surface-1",
        statusColors[ticket.status],
        "active:scale-[0.98] transition-transform",
        className
      ),
      whileTap: { scale: 0.98 },
      onClick,
      children: /* @__PURE__ */ jsxs4("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ jsx5("div", { className: "w-12 h-12 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0", children: /* @__PURE__ */ jsxs4("span", { className: "font-mono text-sm font-bold text-gold-500", children: [
          "#",
          ticket.ticketNumber.toString().slice(-3)
        ] }) }),
        /* @__PURE__ */ jsxs4("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx5("p", { className: "text-sm font-medium text-text-primary truncate", children: ticket.propertyTitle }),
          /* @__PURE__ */ jsx5("p", { className: "text-xs text-text-muted", children: formatTicketNumber(ticket.ticketNumber) })
        ] }),
        /* @__PURE__ */ jsx5("span", { className: cn(
          "status-badge text-xs",
          ticket.status === "active" && "status-active",
          ticket.status === "won" && "status-drawn"
        ), children: statusLabels[ticket.status] })
      ] })
    }
  );
};
var TicketStack = ({
  tickets,
  onTicketClick,
  className
}) => {
  const { isDesktop } = useViewport();
  if (isDesktop) {
    return /* @__PURE__ */ jsx5("div", { className: cn("grid grid-cols-2 xl:grid-cols-3 gap-6", className), children: tickets.map((ticket) => /* @__PURE__ */ jsx5(
      TicketCard,
      {
        ticket,
        onClick: () => onTicketClick?.(ticket)
      },
      ticket.id
    )) });
  }
  return /* @__PURE__ */ jsx5("div", { className: cn("relative", className), children: /* @__PURE__ */ jsx5("div", { className: "flex gap-3 overflow-x-auto snap-x-mandatory pb-4 hide-scrollbar -mx-4 px-4", children: tickets.map((ticket) => /* @__PURE__ */ jsx5("div", { className: "snap-center flex-shrink-0 w-[85%]", children: /* @__PURE__ */ jsx5(
    TicketCard,
    {
      ticket,
      onClick: () => onTicketClick?.(ticket)
    }
  ) }, ticket.id)) }) });
};

// src/components/HeroMedia.tsx
import { useRef as useRef2, useEffect as useEffect3, useState as useState2 } from "react";
import { motion as motion6, useMotionValue as useMotionValue2, useSpring as useSpring2 } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var HeroMedia = ({
  desktopVideo,
  desktopImage,
  mobileVideo,
  mobileImage,
  alt,
  overlay = true,
  parallax = true,
  className,
  children
}) => {
  const { isDesktop, isMobile } = useViewport();
  const { normalizedPosition } = useMousePosition();
  const { ref, isInView } = useInView({ threshold: 0.3 });
  const videoRef = useRef2(null);
  const [isPlaying, setIsPlaying] = useState2(true);
  const [isMuted, setIsMuted] = useState2(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState2(false);
  const mouseX = useSpring2(useMotionValue2(0), { damping: 40, stiffness: 200 });
  const mouseY = useSpring2(useMotionValue2(0), { damping: 40, stiffness: 200 });
  useEffect3(() => {
    if (parallax && isDesktop) {
      mouseX.set(normalizedPosition.x * 3);
      mouseY.set(normalizedPosition.y * 3);
    }
  }, [normalizedPosition, parallax, isDesktop, mouseX, mouseY]);
  useEffect3(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isInView && isPlaying) {
      video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  }, [isInView, isPlaying]);
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  };
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };
  const videoSrc = isDesktop ? desktopVideo : mobileVideo;
  const imageSrc = isDesktop ? desktopImage : mobileImage;
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      ref,
      className: cn(
        "relative overflow-hidden",
        isMobile ? "aspect-hero-mobile" : "aspect-hero-desktop",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs5(
          motion6.div,
          {
            className: "absolute inset-0",
            style: {
              x: parallax && isDesktop ? mouseX : 0,
              y: parallax && isDesktop ? mouseY : 0,
              scale: parallax ? 1.1 : 1
            },
            children: [
              videoSrc && /* @__PURE__ */ jsx6(
                "video",
                {
                  ref: videoRef,
                  className: cn(
                    "absolute inset-0 w-full h-full object-cover",
                    !isVideoLoaded && "opacity-0"
                  ),
                  src: videoSrc,
                  muted: isMuted,
                  loop: true,
                  playsInline: true,
                  onLoadedData: () => setIsVideoLoaded(true)
                }
              ),
              /* @__PURE__ */ jsx6(
                "img",
                {
                  src: imageSrc,
                  alt,
                  className: cn(
                    "absolute inset-0 w-full h-full object-cover",
                    videoSrc && isVideoLoaded && "opacity-0"
                  ),
                  loading: "eager"
                }
              )
            ]
          }
        ),
        overlay && /* @__PURE__ */ jsx6("div", { className: "absolute inset-0 bg-gradient-to-t from-surface-0 via-surface-0/50 to-transparent" }),
        videoSrc && isDesktop && /* @__PURE__ */ jsxs5("div", { className: "absolute bottom-6 right-6 flex gap-2", children: [
          /* @__PURE__ */ jsx6(
            "button",
            {
              onClick: togglePlay,
              className: "p-3 rounded-full bg-surface-0/60 backdrop-blur-sm border border-white/10 hover:bg-surface-0/80 transition-colors",
              "aria-label": isPlaying ? "Pause video" : "Play video",
              children: isPlaying ? /* @__PURE__ */ jsx6(Pause, { className: "w-4 h-4 text-white" }) : /* @__PURE__ */ jsx6(Play, { className: "w-4 h-4 text-white" })
            }
          ),
          /* @__PURE__ */ jsx6(
            "button",
            {
              onClick: toggleMute,
              className: "p-3 rounded-full bg-surface-0/60 backdrop-blur-sm border border-white/10 hover:bg-surface-0/80 transition-colors",
              "aria-label": isMuted ? "Unmute video" : "Mute video",
              children: isMuted ? /* @__PURE__ */ jsx6(VolumeX, { className: "w-4 h-4 text-white" }) : /* @__PURE__ */ jsx6(Volume2, { className: "w-4 h-4 text-white" })
            }
          )
        ] }),
        children && /* @__PURE__ */ jsx6("div", { className: "absolute inset-0 flex items-end", children: /* @__PURE__ */ jsx6("div", { className: "w-full p-6 md:p-12", children }) })
      ]
    }
  );
};

// src/components/PropertyCard.tsx
import { motion as motion7 } from "framer-motion";
import { MapPin, Users, Clock, ChevronRight as ChevronRight2 } from "lucide-react";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var PropertyCard = ({
  property,
  onClick,
  className
}) => {
  const countdown = useCountdown(property.drawDate);
  const progress = calculateProgress(property.ticketsSold, property.totalTickets);
  const statusBadge = {
    active: { class: "status-active", label: "Active" },
    closed: { class: "status-closed", label: "Closed" },
    drawn: { class: "status-drawn", label: "Drawn" }
  };
  return /* @__PURE__ */ jsxs6(
    Card,
    {
      variant: "interactive",
      hover: true,
      glow: property.status === "active",
      className: cn("overflow-hidden", className),
      onClick,
      children: [
        /* @__PURE__ */ jsxs6("div", { className: "relative aspect-video overflow-hidden", children: [
          /* @__PURE__ */ jsx7(
            "img",
            {
              src: property.images[0],
              alt: property.title,
              className: "w-full h-full object-cover transition-transform duration-500 group-hover:scale-105",
              loading: "lazy"
            }
          ),
          /* @__PURE__ */ jsx7("div", { className: "absolute top-4 left-4", children: /* @__PURE__ */ jsx7("span", { className: cn("status-badge", statusBadge[property.status].class), children: statusBadge[property.status].label }) }),
          /* @__PURE__ */ jsxs6("div", { className: "absolute bottom-0 inset-x-0 bg-gradient-to-t from-surface-0/90 to-transparent p-4", children: [
            /* @__PURE__ */ jsx7("p", { className: "text-xs text-text-muted uppercase tracking-wider", children: "Market Value" }),
            /* @__PURE__ */ jsx7("p", { className: "font-mono text-xl font-bold text-text-primary tabular-nums", children: formatNaira(property.marketValue) })
          ] })
        ] }),
        /* @__PURE__ */ jsxs6("div", { className: "p-5", children: [
          /* @__PURE__ */ jsx7("h3", { className: "font-display text-lg font-semibold text-text-primary line-clamp-2 mb-2", children: property.title }),
          /* @__PURE__ */ jsxs6("p", { className: "flex items-center gap-1.5 text-sm text-text-secondary mb-4", children: [
            /* @__PURE__ */ jsx7(MapPin, { className: "w-4 h-4 text-gold-500" }),
            property.location
          ] }),
          /* @__PURE__ */ jsxs6("div", { className: "mb-4", children: [
            /* @__PURE__ */ jsxs6("div", { className: "flex justify-between text-sm mb-2", children: [
              /* @__PURE__ */ jsx7("span", { className: "text-text-muted", children: "Tickets Sold" }),
              /* @__PURE__ */ jsxs6("span", { className: "font-mono text-text-primary tabular-nums", children: [
                formatNumber(property.ticketsSold),
                " / ",
                formatNumber(property.totalTickets)
              ] })
            ] }),
            /* @__PURE__ */ jsx7("div", { className: "progress-bar", children: /* @__PURE__ */ jsx7(
              motion7.div,
              {
                className: "progress-fill",
                initial: { width: 0 },
                animate: { width: `${progress}%` },
                transition: { duration: 1, ease: "easeOut" }
              }
            ) })
          ] }),
          property.status === "active" && !countdown.isExpired && /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 mb-4 p-3 rounded-lg bg-surface-2/50", children: [
            /* @__PURE__ */ jsx7(Clock, { className: "w-4 h-4 text-gold-500" }),
            /* @__PURE__ */ jsx7("span", { className: "text-xs text-text-muted", children: "Draw in:" }),
            /* @__PURE__ */ jsxs6("span", { className: "font-mono text-sm font-semibold text-text-primary tabular-nums", children: [
              countdown.days,
              "d ",
              countdown.hours,
              "h ",
              countdown.minutes,
              "m"
            ] })
          ] }),
          /* @__PURE__ */ jsxs6("div", { className: "flex items-center justify-between pt-4 border-t border-white/5", children: [
            /* @__PURE__ */ jsxs6("div", { children: [
              /* @__PURE__ */ jsx7("p", { className: "text-xs text-text-muted", children: "Per Ticket" }),
              /* @__PURE__ */ jsx7("p", { className: "font-mono text-lg font-bold text-gold-500 tabular-nums", children: formatNaira(property.ticketPrice) })
            ] }),
            /* @__PURE__ */ jsx7(
              motion7.div,
              {
                whileHover: { x: 4 },
                transition: vaultSpring,
                children: /* @__PURE__ */ jsx7(ChevronRight2, { className: "w-6 h-6 text-gold-500" })
              }
            )
          ] })
        ] })
      ]
    }
  );
};
var FeaturedProperty = ({
  property,
  onViewDetails,
  onBuyTicket,
  className
}) => {
  const { isDesktop } = useViewport();
  const countdown = useCountdown(property.drawDate);
  const progress = calculateProgress(property.ticketsSold, property.totalTickets);
  return /* @__PURE__ */ jsxs6("div", { className: cn("relative", className), children: [
    /* @__PURE__ */ jsxs6("div", { className: "absolute inset-0 overflow-hidden rounded-vault-xl", children: [
      /* @__PURE__ */ jsx7(
        "img",
        {
          src: property.images[0],
          alt: "",
          className: "w-full h-full object-cover"
        }
      ),
      /* @__PURE__ */ jsx7("div", { className: "absolute inset-0 bg-gradient-to-r from-surface-0 via-surface-0/80 to-transparent" })
    ] }),
    /* @__PURE__ */ jsxs6("div", { className: cn(
      "relative z-10",
      isDesktop ? "grid grid-cols-2 gap-12 p-12" : "p-6"
    ), children: [
      /* @__PURE__ */ jsxs6("div", { className: "space-y-6", children: [
        /* @__PURE__ */ jsx7("span", { className: "status-badge status-active", children: "Featured Property" }),
        /* @__PURE__ */ jsxs6("div", { children: [
          /* @__PURE__ */ jsx7("h2", { className: "font-display text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-3", children: property.title }),
          /* @__PURE__ */ jsxs6("p", { className: "flex items-center gap-2 text-text-secondary", children: [
            /* @__PURE__ */ jsx7(MapPin, { className: "w-5 h-5 text-gold-500" }),
            property.location
          ] })
        ] }),
        /* @__PURE__ */ jsx7("p", { className: "text-text-secondary line-clamp-3 max-w-lg", children: property.description }),
        /* @__PURE__ */ jsxs6("div", { className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs6("div", { className: "p-4 rounded-vault bg-surface-1/60 backdrop-blur-sm border border-white/10", children: [
            /* @__PURE__ */ jsx7("p", { className: "text-xs text-text-muted uppercase tracking-wider mb-1", children: "Market Value" }),
            /* @__PURE__ */ jsx7("p", { className: "font-mono text-xl font-bold text-text-primary tabular-nums", children: formatNaira(property.marketValue) })
          ] }),
          /* @__PURE__ */ jsxs6("div", { className: "p-4 rounded-vault bg-surface-1/60 backdrop-blur-sm border border-white/10", children: [
            /* @__PURE__ */ jsx7("p", { className: "text-xs text-text-muted uppercase tracking-wider mb-1", children: "Ticket Price" }),
            /* @__PURE__ */ jsx7("p", { className: "font-mono text-xl font-bold text-gold-500 tabular-nums", children: formatNaira(property.ticketPrice) })
          ] })
        ] }),
        /* @__PURE__ */ jsxs6("div", { className: "flex gap-4", children: [
          /* @__PURE__ */ jsx7(ShimmerButton, { size: "lg", onClick: onBuyTicket, children: "Buy Tickets" }),
          /* @__PURE__ */ jsx7(Button, { variant: "outline", size: "lg", onClick: onViewDetails, children: "View Details" })
        ] })
      ] }),
      isDesktop && /* @__PURE__ */ jsx7("div", { className: "flex items-center justify-center", children: /* @__PURE__ */ jsxs6("div", { className: "w-full max-w-sm space-y-6 p-6 rounded-vault-xl bg-surface-1/80 backdrop-blur-sm border border-white/10", children: [
        !countdown.isExpired && /* @__PURE__ */ jsxs6("div", { className: "text-center", children: [
          /* @__PURE__ */ jsx7("p", { className: "text-sm text-text-muted mb-3", children: "Draw Countdown" }),
          /* @__PURE__ */ jsx7("div", { className: "grid grid-cols-4 gap-2", children: [
            { value: countdown.days, label: "Days" },
            { value: countdown.hours, label: "Hours" },
            { value: countdown.minutes, label: "Min" },
            { value: countdown.seconds, label: "Sec" }
          ].map((item) => /* @__PURE__ */ jsxs6("div", { className: "p-3 rounded-lg bg-surface-2 border border-white/5", children: [
            /* @__PURE__ */ jsx7("p", { className: "font-mono text-2xl font-bold text-text-primary tabular-nums", children: item.value.toString().padStart(2, "0") }),
            /* @__PURE__ */ jsx7("p", { className: "text-xs text-text-muted", children: item.label })
          ] }, item.label)) })
        ] }),
        /* @__PURE__ */ jsxs6("div", { children: [
          /* @__PURE__ */ jsxs6("div", { className: "flex justify-between text-sm mb-2", children: [
            /* @__PURE__ */ jsx7("span", { className: "text-text-muted", children: "Tickets Sold" }),
            /* @__PURE__ */ jsxs6("span", { className: "font-mono text-text-primary tabular-nums", children: [
              Math.round(progress),
              "%"
            ] })
          ] }),
          /* @__PURE__ */ jsx7("div", { className: "progress-bar h-3", children: /* @__PURE__ */ jsx7(
            motion7.div,
            {
              className: "progress-fill",
              initial: { width: 0 },
              animate: { width: `${progress}%` },
              transition: { duration: 1.5, ease: "easeOut" }
            }
          ) }),
          /* @__PURE__ */ jsxs6("p", { className: "text-center text-sm text-text-muted mt-2", children: [
            formatNumber(property.ticketsSold),
            " of ",
            formatNumber(property.totalTickets),
            " sold"
          ] })
        ] }),
        /* @__PURE__ */ jsxs6("div", { className: "flex items-center justify-center gap-2 text-sm text-text-secondary", children: [
          /* @__PURE__ */ jsx7(Users, { className: "w-4 h-4 text-gold-500" }),
          /* @__PURE__ */ jsxs6("span", { children: [
            formatNumber(property.ticketsSold),
            " participants"
          ] })
        ] })
      ] }) })
    ] })
  ] });
};
var PropertyGrid = ({
  properties,
  onPropertyClick,
  className
}) => {
  return /* @__PURE__ */ jsx7("div", { className: cn(
    "grid gap-6",
    "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    className
  ), children: properties.map((property) => /* @__PURE__ */ jsx7(
    PropertyCard,
    {
      property,
      onClick: () => onPropertyClick?.(property)
    },
    property.id
  )) });
};

// src/components/PropertyGallery.tsx
import { useState as useState3, useRef as useRef3, useEffect as useEffect4 } from "react";
import { motion as motion8, AnimatePresence as AnimatePresence2 } from "framer-motion";
import { ChevronLeft, ChevronRight as ChevronRight3, X as X2, ZoomIn } from "lucide-react";
import { Fragment as Fragment2, jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
var PropertyGallery = ({
  images,
  className
}) => {
  const { isMobile } = useViewport();
  const [activeIndex, setActiveIndex] = useState3(0);
  const [showLightbox, setShowLightbox] = useState3(false);
  const [lightboxIndex, setLightboxIndex] = useState3(0);
  const scrollRef = useRef3(null);
  const goToImage = (index) => {
    setActiveIndex(index);
  };
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setShowLightbox(true);
  };
  const closeLightbox = () => {
    setShowLightbox(false);
  };
  useEffect4(() => {
    const handleKeyDown = (e) => {
      if (!showLightbox) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") setLightboxIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);
      if (e.key === "ArrowRight") setLightboxIndex((prev) => prev === images.length - 1 ? 0 : prev + 1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showLightbox, images.length]);
  if (isMobile) {
    return /* @__PURE__ */ jsxs7(Fragment2, { children: [
      /* @__PURE__ */ jsxs7("div", { className: cn("relative", className), children: [
        /* @__PURE__ */ jsx8(
          "div",
          {
            ref: scrollRef,
            className: "flex overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4",
            style: { scrollbarWidth: "none", msOverflowStyle: "none" },
            children: images.map((image, index) => /* @__PURE__ */ jsx8(
              "div",
              {
                className: "flex-shrink-0 w-full snap-center pr-4 last:pr-0",
                children: /* @__PURE__ */ jsxs7(
                  "div",
                  {
                    className: "relative aspect-[4/3] rounded-vault-lg overflow-hidden",
                    onClick: () => openLightbox(index),
                    children: [
                      /* @__PURE__ */ jsx8(
                        "img",
                        {
                          src: image.src,
                          alt: image.alt,
                          className: "w-full h-full object-cover",
                          loading: index === 0 ? "eager" : "lazy"
                        }
                      ),
                      /* @__PURE__ */ jsx8("div", { className: "absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" }),
                      /* @__PURE__ */ jsx8("div", { className: "absolute bottom-3 right-3 p-2 rounded-full bg-black/50 backdrop-blur-sm", children: /* @__PURE__ */ jsx8(ZoomIn, { className: "w-4 h-4 text-white" }) })
                    ]
                  }
                )
              },
              index
            ))
          }
        ),
        /* @__PURE__ */ jsx8("div", { className: "flex justify-center gap-2 mt-4", children: images.map((_, index) => /* @__PURE__ */ jsx8(
          "button",
          {
            onClick: () => goToImage(index),
            className: cn(
              "w-2 h-2 rounded-full transition-colors",
              index === activeIndex ? "bg-gold-500" : "bg-white/20"
            )
          },
          index
        )) })
      ] }),
      /* @__PURE__ */ jsx8(AnimatePresence2, { children: showLightbox && /* @__PURE__ */ jsxs7(
        motion8.div,
        {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          className: "fixed inset-0 z-[100] bg-black flex items-center justify-center",
          children: [
            /* @__PURE__ */ jsx8(
              "button",
              {
                onClick: closeLightbox,
                className: "absolute top-4 right-4 p-3 rounded-full bg-white/10 backdrop-blur-sm z-10",
                children: /* @__PURE__ */ jsx8(X2, { className: "w-5 h-5 text-white" })
              }
            ),
            /* @__PURE__ */ jsx8("div", { className: "w-full h-full flex items-center justify-center p-4", children: /* @__PURE__ */ jsx8(
              "img",
              {
                src: images[lightboxIndex].src,
                alt: images[lightboxIndex].alt,
                className: "max-w-full max-h-full object-contain"
              }
            ) }),
            /* @__PURE__ */ jsx8(
              "button",
              {
                onClick: () => setLightboxIndex((prev) => prev === 0 ? images.length - 1 : prev - 1),
                className: "absolute left-4 p-3 rounded-full bg-white/10 backdrop-blur-sm",
                children: /* @__PURE__ */ jsx8(ChevronLeft, { className: "w-5 h-5 text-white" })
              }
            ),
            /* @__PURE__ */ jsx8(
              "button",
              {
                onClick: () => setLightboxIndex((prev) => prev === images.length - 1 ? 0 : prev + 1),
                className: "absolute right-4 p-3 rounded-full bg-white/10 backdrop-blur-sm",
                children: /* @__PURE__ */ jsx8(ChevronRight3, { className: "w-5 h-5 text-white" })
              }
            ),
            /* @__PURE__ */ jsx8("div", { className: "absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm", children: /* @__PURE__ */ jsxs7("span", { className: "text-sm text-white tabular-nums", children: [
              lightboxIndex + 1,
              " / ",
              images.length
            ] }) })
          ]
        }
      ) })
    ] });
  }
  return /* @__PURE__ */ jsxs7(Fragment2, { children: [
    /* @__PURE__ */ jsxs7("div", { className: cn("grid grid-cols-4 grid-rows-2 gap-3 h-[500px]", className), children: [
      /* @__PURE__ */ jsxs7(
        motion8.div,
        {
          className: "col-span-2 row-span-2 relative rounded-vault-lg overflow-hidden cursor-pointer group",
          onClick: () => openLightbox(0),
          whileHover: { scale: 1.01 },
          transition: vaultSpring,
          children: [
            /* @__PURE__ */ jsx8(
              "img",
              {
                src: images[0]?.src,
                alt: images[0]?.alt,
                className: "w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              }
            ),
            /* @__PURE__ */ jsx8("div", { className: "absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" }),
            /* @__PURE__ */ jsx8("div", { className: "absolute bottom-4 right-4 p-2 rounded-full bg-black/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity", children: /* @__PURE__ */ jsx8(ZoomIn, { className: "w-5 h-5 text-white" }) })
          ]
        }
      ),
      images.slice(1, 5).map((image, index) => /* @__PURE__ */ jsxs7(
        motion8.div,
        {
          className: "relative rounded-vault-lg overflow-hidden cursor-pointer group",
          onClick: () => openLightbox(index + 1),
          whileHover: { scale: 1.02 },
          transition: vaultSpring,
          children: [
            /* @__PURE__ */ jsx8(
              "img",
              {
                src: image.src,
                alt: image.alt,
                className: "w-full h-full object-cover transition-transform duration-500 group-hover:scale-110",
                loading: "lazy"
              }
            ),
            /* @__PURE__ */ jsx8("div", { className: "absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" }),
            index === 3 && images.length > 5 && /* @__PURE__ */ jsx8("div", { className: "absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm", children: /* @__PURE__ */ jsxs7("span", { className: "text-white font-display text-xl font-semibold", children: [
              "+",
              images.length - 5,
              " more"
            ] }) })
          ]
        },
        index + 1
      ))
    ] }),
    /* @__PURE__ */ jsx8(AnimatePresence2, { children: showLightbox && /* @__PURE__ */ jsx8(
      motion8.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        className: "fixed inset-0 z-[100] bg-black/95 backdrop-blur-lg flex items-center justify-center",
        onClick: closeLightbox,
        children: /* @__PURE__ */ jsxs7(
          motion8.div,
          {
            initial: { scale: 0.9, opacity: 0 },
            animate: { scale: 1, opacity: 1 },
            exit: { scale: 0.9, opacity: 0 },
            transition: vaultSpring,
            className: "relative max-w-6xl max-h-[90vh] w-full mx-8",
            onClick: (e) => e.stopPropagation(),
            children: [
              /* @__PURE__ */ jsx8(
                "button",
                {
                  onClick: closeLightbox,
                  className: "absolute -top-12 right-0 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors",
                  children: /* @__PURE__ */ jsx8(X2, { className: "w-5 h-5 text-white" })
                }
              ),
              /* @__PURE__ */ jsx8(
                "img",
                {
                  src: images[lightboxIndex].src,
                  alt: images[lightboxIndex].alt,
                  className: "w-full h-full max-h-[80vh] object-contain rounded-vault-lg"
                }
              ),
              /* @__PURE__ */ jsx8(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    setLightboxIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);
                  },
                  className: "absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors",
                  children: /* @__PURE__ */ jsx8(ChevronLeft, { className: "w-6 h-6 text-white" })
                }
              ),
              /* @__PURE__ */ jsx8(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    setLightboxIndex((prev) => prev === images.length - 1 ? 0 : prev + 1);
                  },
                  className: "absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors",
                  children: /* @__PURE__ */ jsx8(ChevronRight3, { className: "w-6 h-6 text-white" })
                }
              ),
              /* @__PURE__ */ jsx8("div", { className: "flex justify-center gap-2 mt-4 overflow-x-auto py-2", children: images.map((image, index) => /* @__PURE__ */ jsx8(
                "button",
                {
                  onClick: () => setLightboxIndex(index),
                  className: cn(
                    "flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-colors",
                    index === lightboxIndex ? "border-gold-500" : "border-transparent opacity-60 hover:opacity-100"
                  ),
                  children: /* @__PURE__ */ jsx8(
                    "img",
                    {
                      src: image.src,
                      alt: "",
                      className: "w-full h-full object-cover"
                    }
                  )
                },
                index
              )) })
            ]
          }
        )
      }
    ) })
  ] });
};

// src/components/Form.tsx
import { forwardRef as forwardRef4 } from "react";
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var inputVariants = cva2(
  "w-full bg-surface-2 border rounded-vault px-4 py-3 text-text-primary placeholder:text-text-muted transition-all duration-200 focus:outline-none",
  {
    variants: {
      variant: {
        default: "border-white/10 focus:border-gold-500/50 focus:ring-2 focus:ring-gold-500/20",
        error: "border-vault-error/50 focus:border-vault-error focus:ring-2 focus:ring-vault-error/20",
        success: "border-vault-success/50 focus:border-vault-success focus:ring-2 focus:ring-vault-success/20"
      },
      inputSize: {
        sm: "h-10 text-sm",
        md: "h-12 text-base",
        lg: "h-14 text-lg"
      }
    },
    defaultVariants: {
      variant: "default",
      inputSize: "md"
    }
  }
);
var Input = forwardRef4(
  ({ className, variant, inputSize, label, error, hint, leftIcon, rightIcon, id, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorVariant = error ? "error" : variant;
    return /* @__PURE__ */ jsxs8("div", { className: "space-y-2", children: [
      label && /* @__PURE__ */ jsx9(
        "label",
        {
          htmlFor: inputId,
          className: "block text-sm font-medium text-text-secondary",
          children: label
        }
      ),
      /* @__PURE__ */ jsxs8("div", { className: "relative", children: [
        leftIcon && /* @__PURE__ */ jsx9("div", { className: "absolute left-4 top-1/2 -translate-y-1/2 text-text-muted", children: leftIcon }),
        /* @__PURE__ */ jsx9(
          "input",
          {
            ref,
            id: inputId,
            className: cn(
              inputVariants({ variant: errorVariant, inputSize }),
              leftIcon && "pl-12",
              rightIcon && "pr-12",
              className
            ),
            ...props
          }
        ),
        rightIcon && /* @__PURE__ */ jsx9("div", { className: "absolute right-4 top-1/2 -translate-y-1/2 text-text-muted", children: rightIcon })
      ] }),
      error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-vault-error", children: error }),
      hint && !error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-text-muted", children: hint })
    ] });
  }
);
Input.displayName = "Input";
var Textarea = forwardRef4(
  ({ className, variant, label, error, hint, id, ...props }, ref) => {
    const inputId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const errorVariant = error ? "error" : variant;
    return /* @__PURE__ */ jsxs8("div", { className: "space-y-2", children: [
      label && /* @__PURE__ */ jsx9(
        "label",
        {
          htmlFor: inputId,
          className: "block text-sm font-medium text-text-secondary",
          children: label
        }
      ),
      /* @__PURE__ */ jsx9(
        "textarea",
        {
          ref,
          id: inputId,
          className: cn(
            inputVariants({ variant: errorVariant }),
            "min-h-[120px] resize-y",
            className
          ),
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-vault-error", children: error }),
      hint && !error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-text-muted", children: hint })
    ] });
  }
);
Textarea.displayName = "Textarea";
var Select = forwardRef4(
  ({ className, variant, inputSize, label, error, hint, options, placeholder, id, ...props }, ref) => {
    const inputId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorVariant = error ? "error" : variant;
    return /* @__PURE__ */ jsxs8("div", { className: "space-y-2", children: [
      label && /* @__PURE__ */ jsx9(
        "label",
        {
          htmlFor: inputId,
          className: "block text-sm font-medium text-text-secondary",
          children: label
        }
      ),
      /* @__PURE__ */ jsxs8(
        "select",
        {
          ref,
          id: inputId,
          className: cn(
            inputVariants({ variant: errorVariant, inputSize }),
            "appearance-none cursor-pointer",
            `bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23A1A1AA'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")] bg-no-repeat bg-[right_1rem_center] bg-[length:1.25rem_1.25rem]`,
            className
          ),
          ...props,
          children: [
            placeholder && /* @__PURE__ */ jsx9("option", { value: "", disabled: true, children: placeholder }),
            options.map((option) => /* @__PURE__ */ jsx9(
              "option",
              {
                value: option.value,
                disabled: option.disabled,
                children: option.label
              },
              option.value
            ))
          ]
        }
      ),
      error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-vault-error", children: error }),
      hint && !error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-text-muted", children: hint })
    ] });
  }
);
Select.displayName = "Select";
var Checkbox = forwardRef4(
  ({ className, label, description, error, id, ...props }, ref) => {
    const inputId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs8("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs8("div", { className: "flex items-start gap-3", children: [
        /* @__PURE__ */ jsx9(
          "input",
          {
            ref,
            type: "checkbox",
            id: inputId,
            className: cn(
              "mt-1 h-5 w-5 rounded border-white/20 bg-surface-2",
              "text-gold-500 focus:ring-gold-500/20 focus:ring-2 focus:ring-offset-0",
              "cursor-pointer",
              className
            ),
            ...props
          }
        ),
        (label || description) && /* @__PURE__ */ jsxs8("div", { children: [
          label && /* @__PURE__ */ jsx9(
            "label",
            {
              htmlFor: inputId,
              className: "text-sm font-medium text-text-primary cursor-pointer",
              children: label
            }
          ),
          description && /* @__PURE__ */ jsx9("p", { className: "text-sm text-text-muted", children: description })
        ] })
      ] }),
      error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-vault-error ml-8", children: error })
    ] });
  }
);
Checkbox.displayName = "Checkbox";
var NumberInput = forwardRef4(
  ({ value, onChange, min = 1, max = 999, step = 1, className, ...props }, ref) => {
    const handleDecrement = () => {
      const newValue = Math.max(min, value - step);
      onChange(newValue);
    };
    const handleIncrement = () => {
      const newValue = Math.min(max, value + step);
      onChange(newValue);
    };
    const handleChange = (e) => {
      const newValue = parseInt(e.target.value) || min;
      onChange(Math.min(max, Math.max(min, newValue)));
    };
    return /* @__PURE__ */ jsxs8("div", { className: "space-y-2", children: [
      props.label && /* @__PURE__ */ jsx9("label", { className: "block text-sm font-medium text-text-secondary", children: props.label }),
      /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx9(
          "button",
          {
            type: "button",
            onClick: handleDecrement,
            disabled: value <= min,
            className: cn(
              "h-12 w-12 rounded-vault bg-surface-2 border border-white/10",
              "flex items-center justify-center text-xl font-semibold text-text-primary",
              "hover:bg-surface-3 transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            ),
            children: "\u2212"
          }
        ),
        /* @__PURE__ */ jsx9(
          "input",
          {
            ref,
            type: "number",
            value,
            onChange: handleChange,
            min,
            max,
            step,
            className: cn(
              "w-20 h-12 bg-surface-2 border border-white/10 rounded-vault",
              "text-center font-mono text-lg font-semibold text-text-primary tabular-nums",
              "focus:outline-none focus:border-gold-500/50 focus:ring-2 focus:ring-gold-500/20",
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
              className
            )
          }
        ),
        /* @__PURE__ */ jsx9(
          "button",
          {
            type: "button",
            onClick: handleIncrement,
            disabled: value >= max,
            className: cn(
              "h-12 w-12 rounded-vault bg-surface-2 border border-white/10",
              "flex items-center justify-center text-xl font-semibold text-text-primary",
              "hover:bg-surface-3 transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            ),
            children: "+"
          }
        )
      ] })
    ] });
  }
);
NumberInput.displayName = "NumberInput";
var Switch = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = "md",
  className
}) => {
  const sizes = {
    sm: { track: "w-8 h-4", thumb: "w-3 h-3", translate: "translate-x-4" },
    md: { track: "w-11 h-6", thumb: "w-5 h-5", translate: "translate-x-5" },
    lg: { track: "w-14 h-7", thumb: "w-6 h-6", translate: "translate-x-7" }
  };
  const { track, thumb, translate } = sizes[size];
  return /* @__PURE__ */ jsxs8("label", { className: cn("flex items-center gap-3 cursor-pointer", disabled && "cursor-not-allowed opacity-50", className), children: [
    /* @__PURE__ */ jsx9(
      "button",
      {
        type: "button",
        role: "switch",
        "aria-checked": checked,
        disabled,
        onClick: () => !disabled && onChange(!checked),
        className: cn(
          "relative inline-flex flex-shrink-0 rounded-full transition-colors duration-200",
          track,
          checked ? "bg-gold-500" : "bg-surface-3"
        ),
        children: /* @__PURE__ */ jsx9(
          "span",
          {
            className: cn(
              "pointer-events-none inline-block rounded-full bg-white shadow-lg transform transition-transform duration-200",
              thumb,
              "absolute top-0.5 left-0.5",
              checked && translate
            )
          }
        )
      }
    ),
    (label || description) && /* @__PURE__ */ jsxs8("div", { className: "flex-1", children: [
      label && /* @__PURE__ */ jsx9("p", { className: "text-sm font-medium text-text-primary", children: label }),
      description && /* @__PURE__ */ jsx9("p", { className: "text-xs text-text-muted", children: description })
    ] })
  ] });
};

// src/components/Feedback.tsx
import React9 from "react";
import { motion as motion9, AnimatePresence as AnimatePresence3 } from "framer-motion";
import { CheckCircle, XCircle, AlertCircle, Info, X as X3 } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var toastIcons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertCircle,
  info: Info
};
var toastStyles = {
  success: "bg-vault-success/20 border-vault-success/30 text-vault-success",
  error: "bg-vault-error/20 border-vault-error/30 text-vault-error",
  warning: "bg-vault-warning/20 border-vault-warning/30 text-vault-warning",
  info: "bg-vault-info/20 border-vault-info/30 text-vault-info"
};
var Toast = ({
  id,
  type,
  title,
  message,
  duration = 5e3,
  onDismiss
}) => {
  const Icon = toastIcons[type];
  React9.useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => onDismiss(id), duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);
  return /* @__PURE__ */ jsxs9(
    motion9.div,
    {
      layout: true,
      initial: { opacity: 0, x: 50, scale: 0.9 },
      animate: { opacity: 1, x: 0, scale: 1 },
      exit: { opacity: 0, x: 50, scale: 0.9 },
      transition: vaultSpring,
      className: cn(
        "relative flex items-start gap-3 p-4 rounded-vault border backdrop-blur-sm",
        "min-w-[300px] max-w-[400px]",
        toastStyles[type]
      ),
      children: [
        /* @__PURE__ */ jsx10(Icon, { className: "w-5 h-5 flex-shrink-0 mt-0.5" }),
        /* @__PURE__ */ jsxs9("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx10("p", { className: "font-medium text-text-primary", children: title }),
          message && /* @__PURE__ */ jsx10("p", { className: "text-sm text-text-secondary mt-1", children: message })
        ] }),
        /* @__PURE__ */ jsx10(
          "button",
          {
            onClick: () => onDismiss(id),
            className: "p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0",
            children: /* @__PURE__ */ jsx10(X3, { className: "w-4 h-4 text-text-secondary" })
          }
        )
      ]
    }
  );
};
var ToastContainer = ({
  toasts,
  onDismiss,
  position = "top-right"
}) => {
  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4"
  };
  return /* @__PURE__ */ jsx10(
    "div",
    {
      className: cn(
        "fixed z-toast flex flex-col gap-3",
        positionClasses[position]
      ),
      children: /* @__PURE__ */ jsx10(AnimatePresence3, { mode: "popLayout", children: toasts.map((toast) => /* @__PURE__ */ jsx10(Toast, { ...toast, onDismiss }, toast.id)) })
    }
  );
};
var ToastContext = React9.createContext(null);
var ToastProvider = ({ children }) => {
  const [toasts, setToasts] = React9.useState([]);
  const addToast = React9.useCallback((toast) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);
  const removeToast = React9.useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const clearToasts = React9.useCallback(() => {
    setToasts([]);
  }, []);
  return /* @__PURE__ */ jsxs9(ToastContext.Provider, { value: { toasts, addToast, removeToast, clearToasts }, children: [
    children,
    /* @__PURE__ */ jsx10(ToastContainer, { toasts, onDismiss: removeToast })
  ] });
};
var useToast = () => {
  const context = React9.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  const { addToast } = context;
  return {
    ...context,
    success: (title, message) => addToast({ type: "success", title, message }),
    error: (title, message) => addToast({ type: "error", title, message }),
    warning: (title, message) => addToast({ type: "warning", title, message }),
    info: (title, message) => addToast({ type: "info", title, message })
  };
};
var Spinner = ({ size = "md", className }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12"
  };
  return /* @__PURE__ */ jsxs9(
    "svg",
    {
      className: cn("animate-spin text-gold-500", sizeClasses[size], className),
      viewBox: "0 0 24 24",
      fill: "none",
      children: [
        /* @__PURE__ */ jsx10(
          "circle",
          {
            className: "opacity-25",
            cx: "12",
            cy: "12",
            r: "10",
            stroke: "currentColor",
            strokeWidth: "4"
          }
        ),
        /* @__PURE__ */ jsx10(
          "path",
          {
            className: "opacity-75",
            fill: "currentColor",
            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          }
        )
      ]
    }
  );
};
var Skeleton = ({
  className,
  variant = "rectangular"
}) => {
  const variantClasses = {
    text: "h-4 rounded",
    circular: "rounded-full",
    rectangular: "rounded-vault"
  };
  return /* @__PURE__ */ jsx10(
    "div",
    {
      className: cn(
        "animate-pulse bg-surface-2",
        variantClasses[variant],
        className
      )
    }
  );
};
var Badge = ({
  variant = "default",
  children,
  className
}) => {
  const variants = {
    default: "bg-surface-2 text-text-secondary border-white/10",
    gold: "bg-gold-500/20 text-gold-500 border-gold-500/30",
    success: "bg-vault-success/20 text-vault-success border-vault-success/30",
    error: "bg-vault-error/20 text-vault-error border-vault-error/30",
    warning: "bg-vault-warning/20 text-vault-warning border-vault-warning/30"
  };
  return /* @__PURE__ */ jsx10(
    "span",
    {
      className: cn(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border",
        variants[variant],
        className
      ),
      children
    }
  );
};
var Avatar = ({
  src,
  alt,
  fallback,
  size = "md",
  className
}) => {
  const [error, setError] = React9.useState(false);
  const sizeClasses = {
    sm: "w-8 h-8 text-xs",
    md: "w-10 h-10 text-sm",
    lg: "w-12 h-12 text-base",
    xl: "w-16 h-16 text-lg"
  };
  const getFallbackInitials = () => {
    if (fallback) return fallback.slice(0, 2).toUpperCase();
    if (alt) return alt.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
    return "??";
  };
  return /* @__PURE__ */ jsx10(
    "div",
    {
      className: cn(
        "relative rounded-full bg-surface-2 flex items-center justify-center overflow-hidden",
        sizeClasses[size],
        className
      ),
      children: src && !error ? /* @__PURE__ */ jsx10(
        "img",
        {
          src,
          alt,
          className: "w-full h-full object-cover",
          onError: () => setError(true)
        }
      ) : /* @__PURE__ */ jsx10("span", { className: "font-medium text-text-secondary", children: getFallbackInitials() })
    }
  );
};

// src/components/Navigation.tsx
import React10 from "react";
import { motion as motion10, AnimatePresence as AnimatePresence4 } from "framer-motion";
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var DesktopNav = ({
  logo,
  items,
  activeItem,
  onItemClick,
  rightContent,
  className
}) => {
  const { scrollPosition, scrollDirection } = useScrollPosition();
  const isScrolled = scrollPosition.y > 50;
  const isHidden = scrollDirection === "down" && scrollPosition.y > 200;
  return /* @__PURE__ */ jsx11(
    motion10.header,
    {
      initial: { y: 0 },
      animate: { y: isHidden ? -100 : 0 },
      transition: vaultSpring,
      className: cn(
        "fixed top-0 left-0 right-0 z-sticky-bar",
        "transition-all duration-300",
        isScrolled ? "glass-nav" : "bg-transparent",
        className
      ),
      children: /* @__PURE__ */ jsxs10("nav", { className: "max-w-7xl mx-auto px-6 h-20 flex items-center justify-between", children: [
        /* @__PURE__ */ jsx11("div", { className: "flex-shrink-0", children: logo }),
        /* @__PURE__ */ jsx11("div", { className: "hidden md:flex items-center gap-1", children: items.map((item) => /* @__PURE__ */ jsxs10(
          "button",
          {
            onClick: () => onItemClick?.(item),
            className: cn(
              "relative px-4 py-2 rounded-vault text-sm font-medium transition-colors",
              activeItem === item.id ? "text-text-primary" : "text-text-secondary hover:text-text-primary"
            ),
            children: [
              item.label,
              activeItem === item.id && /* @__PURE__ */ jsx11(
                motion10.div,
                {
                  layoutId: "nav-indicator",
                  className: "absolute inset-0 bg-white/5 rounded-vault",
                  transition: vaultSpring
                }
              ),
              item.badge !== void 0 && item.badge > 0 && /* @__PURE__ */ jsx11("span", { className: "absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold-500 text-surface-0 text-xs font-bold flex items-center justify-center", children: item.badge > 99 ? "99+" : item.badge })
            ]
          },
          item.id
        )) }),
        /* @__PURE__ */ jsx11("div", { className: "flex items-center gap-4", children: rightContent })
      ] })
    }
  );
};
var MobileNav = ({
  items,
  activeItem,
  onItemClick,
  className
}) => {
  return /* @__PURE__ */ jsx11(
    "nav",
    {
      className: cn(
        "fixed bottom-0 left-0 right-0 z-bottom-nav",
        "bg-surface-1/95 backdrop-blur-glass border-t border-white/10",
        "safe-area-bottom",
        className
      ),
      children: /* @__PURE__ */ jsx11("div", { className: "flex items-center justify-around h-16 px-2", children: items.map((item) => /* @__PURE__ */ jsxs10(
        "button",
        {
          onClick: () => onItemClick?.(item),
          className: cn(
            "relative flex flex-col items-center justify-center w-full h-full",
            "transition-colors touch-active"
          ),
          children: [
            /* @__PURE__ */ jsxs10(
              "div",
              {
                className: cn(
                  "relative w-6 h-6 mb-1 transition-colors",
                  activeItem === item.id ? "text-gold-500" : "text-text-muted"
                ),
                children: [
                  item.icon,
                  activeItem === item.id && /* @__PURE__ */ jsx11(
                    motion10.div,
                    {
                      layoutId: "mobile-nav-indicator",
                      className: "absolute -inset-2 bg-gold-500/20 rounded-full -z-10",
                      transition: vaultSpring
                    }
                  ),
                  item.badge !== void 0 && item.badge > 0 && /* @__PURE__ */ jsx11("span", { className: "absolute -top-2 -right-2 min-w-[18px] h-[18px] rounded-full bg-vault-error text-white text-[10px] font-bold flex items-center justify-center px-1", children: item.badge > 99 ? "99+" : item.badge })
                ]
              }
            ),
            /* @__PURE__ */ jsx11(
              "span",
              {
                className: cn(
                  "text-[10px] font-medium transition-colors",
                  activeItem === item.id ? "text-gold-500" : "text-text-muted"
                ),
                children: item.label
              }
            )
          ]
        },
        item.id
      )) })
    }
  );
};
var ResponsiveNav = (props) => {
  const { isDesktop } = useViewport();
  if (isDesktop) {
    return /* @__PURE__ */ jsx11(DesktopNav, { ...props });
  }
  return /* @__PURE__ */ jsx11(MobileNav, { ...props });
};
var StickyActionBar = ({
  children,
  show = true,
  className
}) => {
  return /* @__PURE__ */ jsx11(AnimatePresence4, { children: show && /* @__PURE__ */ jsx11(
    motion10.div,
    {
      initial: { y: 100, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: 100, opacity: 0 },
      transition: vaultSpring,
      className: cn(
        "fixed bottom-0 left-0 right-0 z-sticky-bar",
        "bg-surface-1/95 backdrop-blur-glass border-t border-white/10",
        "p-4 pb-safe",
        // Account for mobile nav if present
        "mb-16 md:mb-0",
        className
      ),
      children
    }
  ) });
};
var Breadcrumb = ({
  items,
  onItemClick,
  className
}) => {
  return /* @__PURE__ */ jsx11("nav", { className: cn("flex items-center gap-2 text-sm", className), children: items.map((item, index) => /* @__PURE__ */ jsxs10(React10.Fragment, { children: [
    index > 0 && /* @__PURE__ */ jsx11("span", { className: "text-text-muted", children: "/" }),
    item.href && index < items.length - 1 ? /* @__PURE__ */ jsx11(
      "button",
      {
        onClick: () => onItemClick?.(item, index),
        className: "text-text-secondary hover:text-text-primary transition-colors",
        children: item.label
      }
    ) : /* @__PURE__ */ jsx11("span", { className: "text-text-primary font-medium", children: item.label })
  ] }, index)) });
};

// src/components/DataTable.tsx
import { motion as motion11 } from "framer-motion";
import {
  ChevronLeft as ChevronLeft2,
  ChevronRight as ChevronRight4,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
  Download,
  RefreshCw
} from "lucide-react";
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
function DataTable({
  columns,
  data,
  loading = false,
  emptyMessage = "No data available",
  sortColumn,
  sortDirection,
  onSort,
  onRowClick,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  getRowId,
  className
}) {
  const renderSortIcon = (columnId) => {
    if (sortColumn !== columnId) {
      return /* @__PURE__ */ jsx12(ArrowUpDown, { className: "w-4 h-4 opacity-50" });
    }
    return sortDirection === "asc" ? /* @__PURE__ */ jsx12(ArrowUp, { className: "w-4 h-4 text-gold-500" }) : /* @__PURE__ */ jsx12(ArrowDown, { className: "w-4 h-4 text-gold-500" });
  };
  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right"
  };
  if (loading) {
    return /* @__PURE__ */ jsx12("div", { className: cn("overflow-hidden rounded-vault border border-white/10", className), children: /* @__PURE__ */ jsxs11("table", { className: "data-table", children: [
      /* @__PURE__ */ jsx12("thead", { className: "bg-surface-2", children: /* @__PURE__ */ jsx12("tr", { children: columns.map((column) => /* @__PURE__ */ jsx12("th", { style: { width: column.width }, children: /* @__PURE__ */ jsx12(Skeleton, { className: "h-4 w-24" }) }, column.id)) }) }),
      /* @__PURE__ */ jsx12("tbody", { children: [...Array(5)].map((_, i) => /* @__PURE__ */ jsx12("tr", { children: columns.map((column) => /* @__PURE__ */ jsx12("td", { children: /* @__PURE__ */ jsx12(Skeleton, { className: "h-4 w-full" }) }, column.id)) }, i)) })
    ] }) });
  }
  return /* @__PURE__ */ jsx12("div", { className: cn("overflow-hidden rounded-vault border border-white/10", className), children: /* @__PURE__ */ jsx12("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs11("table", { className: "data-table", children: [
    /* @__PURE__ */ jsx12("thead", { className: "bg-surface-2", children: /* @__PURE__ */ jsxs11("tr", { children: [
      onSelectRow && /* @__PURE__ */ jsx12("th", { className: "w-12 px-4", children: /* @__PURE__ */ jsx12(
        "input",
        {
          type: "checkbox",
          checked: data.length > 0 && selectedRows.length === data.length,
          onChange: onSelectAll,
          className: "h-4 w-4 rounded border-white/20 bg-surface-1 text-gold-500 focus:ring-gold-500/20"
        }
      ) }),
      columns.map((column) => /* @__PURE__ */ jsx12(
        "th",
        {
          style: { width: column.width },
          className: alignClasses[column.align || "left"],
          children: column.sortable ? /* @__PURE__ */ jsxs11(
            "button",
            {
              onClick: () => onSort?.(column.id),
              className: "flex items-center gap-2 hover:text-text-primary transition-colors",
              children: [
                column.header,
                renderSortIcon(column.id)
              ]
            }
          ) : column.header
        },
        column.id
      ))
    ] }) }),
    /* @__PURE__ */ jsx12("tbody", { children: data.length === 0 ? /* @__PURE__ */ jsx12("tr", { children: /* @__PURE__ */ jsx12(
      "td",
      {
        colSpan: columns.length + (onSelectRow ? 1 : 0),
        className: "text-center py-12 text-text-muted",
        children: emptyMessage
      }
    ) }) : data.map((row, rowIndex) => {
      const rowId = getRowId?.(row) || String(rowIndex);
      const isSelected = selectedRows.includes(rowId);
      return /* @__PURE__ */ jsxs11(
        motion11.tr,
        {
          initial: { opacity: 0, y: 10 },
          animate: { opacity: 1, y: 0 },
          transition: { delay: rowIndex * 0.02 },
          onClick: () => onRowClick?.(row),
          className: cn(
            onRowClick && "cursor-pointer",
            isSelected && "bg-gold-500/10"
          ),
          children: [
            onSelectRow && /* @__PURE__ */ jsx12("td", { className: "w-12 px-4", children: /* @__PURE__ */ jsx12(
              "input",
              {
                type: "checkbox",
                checked: isSelected,
                onChange: (e) => {
                  e.stopPropagation();
                  onSelectRow(rowId);
                },
                className: "h-4 w-4 rounded border-white/20 bg-surface-1 text-gold-500 focus:ring-gold-500/20"
              }
            ) }),
            columns.map((column) => /* @__PURE__ */ jsx12(
              "td",
              {
                className: alignClasses[column.align || "left"],
                children: column.cell ? column.cell(row) : column.accessorKey ? String(row[column.accessorKey] ?? "") : null
              },
              column.id
            ))
          ]
        },
        rowId
      );
    }) })
  ] }) }) });
}
var TableToolbar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  filters,
  actions,
  onRefresh,
  onExport,
  className
}) => {
  return /* @__PURE__ */ jsxs11("div", { className: cn("flex flex-col sm:flex-row gap-4 mb-4", className), children: [
    onSearchChange && /* @__PURE__ */ jsx12("div", { className: "flex-1 max-w-sm", children: /* @__PURE__ */ jsx12(
      Input,
      {
        value: searchValue,
        onChange: (e) => onSearchChange(e.target.value),
        placeholder: searchPlaceholder,
        inputSize: "sm",
        leftIcon: /* @__PURE__ */ jsx12(Search, { className: "w-4 h-4" })
      }
    ) }),
    filters && /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx12(Filter, { className: "w-4 h-4 text-text-muted" }),
      filters
    ] }),
    /* @__PURE__ */ jsx12("div", { className: "flex-1" }),
    /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
      onRefresh && /* @__PURE__ */ jsx12(Button, { variant: "ghost", size: "icon", onClick: onRefresh, children: /* @__PURE__ */ jsx12(RefreshCw, { className: "w-4 h-4" }) }),
      onExport && /* @__PURE__ */ jsx12(Button, { variant: "ghost", size: "icon", onClick: onExport, children: /* @__PURE__ */ jsx12(Download, { className: "w-4 h-4" }) }),
      actions
    ] })
  ] });
};
var Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  className
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    pages.push(1);
    if (currentPage > 3) {
      pages.push("ellipsis");
    }
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) {
      pages.push("ellipsis");
    }
    pages.push(totalPages);
    return pages;
  };
  return /* @__PURE__ */ jsxs11("div", { className: cn("flex flex-col sm:flex-row items-center justify-between gap-4 mt-4", className), children: [
    /* @__PURE__ */ jsxs11("p", { className: "text-sm text-text-muted", children: [
      "Showing ",
      startItem,
      " to ",
      endItem,
      " of ",
      totalItems,
      " results"
    ] }),
    onPageSizeChange && /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx12("span", { className: "text-sm text-text-muted", children: "Rows per page:" }),
      /* @__PURE__ */ jsx12(
        Select,
        {
          value: String(pageSize),
          onChange: (e) => onPageSizeChange(Number(e.target.value)),
          options: pageSizeOptions.map((size) => ({
            value: String(size),
            label: String(size)
          })),
          inputSize: "sm",
          className: "w-20"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx12(
        Button,
        {
          variant: "ghost",
          size: "icon",
          onClick: () => onPageChange(1),
          disabled: currentPage === 1,
          children: /* @__PURE__ */ jsx12(ChevronsLeft, { className: "w-4 h-4" })
        }
      ),
      /* @__PURE__ */ jsx12(
        Button,
        {
          variant: "ghost",
          size: "icon",
          onClick: () => onPageChange(currentPage - 1),
          disabled: currentPage === 1,
          children: /* @__PURE__ */ jsx12(ChevronLeft2, { className: "w-4 h-4" })
        }
      ),
      /* @__PURE__ */ jsx12("div", { className: "flex items-center gap-1", children: getPageNumbers().map(
        (page, index) => page === "ellipsis" ? /* @__PURE__ */ jsx12("span", { className: "px-2 text-text-muted", children: /* @__PURE__ */ jsx12(MoreHorizontal, { className: "w-4 h-4" }) }, `ellipsis-${index}`) : /* @__PURE__ */ jsx12(
          Button,
          {
            variant: currentPage === page ? "gold" : "ghost",
            size: "icon",
            onClick: () => onPageChange(page),
            children: page
          },
          page
        )
      ) }),
      /* @__PURE__ */ jsx12(
        Button,
        {
          variant: "ghost",
          size: "icon",
          onClick: () => onPageChange(currentPage + 1),
          disabled: currentPage === totalPages,
          children: /* @__PURE__ */ jsx12(ChevronRight4, { className: "w-4 h-4" })
        }
      ),
      /* @__PURE__ */ jsx12(
        Button,
        {
          variant: "ghost",
          size: "icon",
          onClick: () => onPageChange(totalPages),
          disabled: currentPage === totalPages,
          children: /* @__PURE__ */ jsx12(ChevronsRight, { className: "w-4 h-4" })
        }
      )
    ] })
  ] });
};
var StatCard = ({
  title,
  value,
  change,
  icon,
  className
}) => {
  const trendColors = {
    up: "text-vault-success",
    down: "text-vault-error",
    neutral: "text-text-muted"
  };
  const trendIcons = {
    up: /* @__PURE__ */ jsx12(ArrowUp, { className: "w-4 h-4" }),
    down: /* @__PURE__ */ jsx12(ArrowDown, { className: "w-4 h-4" }),
    neutral: null
  };
  return /* @__PURE__ */ jsx12("div", { className: cn("p-6 rounded-vault-lg bg-surface-1 border border-white/5", className), children: /* @__PURE__ */ jsxs11("div", { className: "flex items-start justify-between", children: [
    /* @__PURE__ */ jsxs11("div", { children: [
      /* @__PURE__ */ jsx12("p", { className: "text-sm text-text-muted mb-1", children: title }),
      /* @__PURE__ */ jsx12("p", { className: "font-mono text-2xl font-bold text-text-primary tabular-nums", children: value }),
      change && /* @__PURE__ */ jsxs11("div", { className: cn("flex items-center gap-1 mt-2 text-sm", trendColors[change.trend]), children: [
        trendIcons[change.trend],
        /* @__PURE__ */ jsxs11("span", { children: [
          Math.abs(change.value),
          "%"
        ] }),
        /* @__PURE__ */ jsx12("span", { className: "text-text-muted", children: "vs last period" })
      ] })
    ] }),
    icon && /* @__PURE__ */ jsx12("div", { className: "w-12 h-12 rounded-vault bg-gold-500/10 flex items-center justify-center text-gold-500", children: icon })
  ] }) });
};

// src/components/TabsAndEmpty.tsx
import { Inbox } from "lucide-react";
import { jsx as jsx13, jsxs as jsxs12 } from "react/jsx-runtime";
var EmptyState = ({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className
}) => {
  const ActionWrapper = action?.href ? "a" : "button";
  return /* @__PURE__ */ jsxs12("div", { className: cn(
    "flex flex-col items-center justify-center text-center py-16 px-6",
    className
  ), children: [
    /* @__PURE__ */ jsx13("div", { className: "w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center mb-6", children: /* @__PURE__ */ jsx13(Icon, { className: "w-8 h-8 text-text-muted" }) }),
    /* @__PURE__ */ jsx13("h3", { className: "font-display text-xl font-semibold text-text-primary mb-2", children: title }),
    /* @__PURE__ */ jsx13("p", { className: "text-text-secondary max-w-sm mb-6", children: description }),
    action && /* @__PURE__ */ jsx13(
      ActionWrapper,
      {
        href: action.href,
        onClick: action.onClick,
        className: "px-6 py-2.5 rounded-vault bg-gold-500 hover:bg-gold-400 text-black font-medium transition-colors",
        children: action.label
      }
    )
  ] });
};
var Tabs = ({
  value,
  activeTab,
  onChange,
  onTabChange,
  tabs,
  items,
  className
}) => {
  const tabList = tabs || items || [];
  const activeValue = value || activeTab;
  const handleChange = onChange || onTabChange;
  return /* @__PURE__ */ jsx13("div", { className: cn(
    "flex gap-1 p-1 rounded-vault-lg bg-surface-2/50 border border-white/5 overflow-x-auto",
    className
  ), children: tabList.map((tab) => {
    const tabValue = tab.value || tab.id || "";
    const badgeValue = tab.badge ?? tab.count;
    return /* @__PURE__ */ jsxs12(
      "button",
      {
        onClick: () => handleChange?.(tabValue),
        className: cn(
          "flex-1 min-w-max flex items-center justify-center gap-2 px-4 py-2 rounded-vault text-sm font-medium transition-colors whitespace-nowrap",
          activeValue === tabValue ? "bg-surface-3 text-text-primary" : "text-text-muted hover:text-text-secondary"
        ),
        children: [
          tab.label,
          badgeValue !== void 0 && /* @__PURE__ */ jsx13("span", { className: cn(
            "px-1.5 py-0.5 rounded-full text-xs tabular-nums",
            activeValue === tabValue ? "bg-gold-500/20 text-gold-500" : "bg-surface-3 text-text-muted"
          ), children: badgeValue })
        ]
      },
      tabValue
    );
  }) });
};
export {
  Avatar,
  Badge,
  BottomSheet,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  DataTable,
  DesktopNav,
  EmptyState,
  FeaturedProperty,
  HeroMedia,
  Input,
  MobileNav,
  Modal,
  NumberInput,
  Pagination,
  PropertyCard,
  PropertyGallery,
  PropertyGrid,
  ResponsiveDialog,
  ResponsiveNav,
  Select,
  ShimmerButton,
  Skeleton,
  SlideToConfirm,
  Spinner,
  StatCard,
  StickyActionBar,
  Switch,
  TableToolbar,
  Tabs,
  Textarea,
  TicketCard,
  TicketStack,
  ToastContainer,
  ToastProvider,
  calculateProgress,
  cn,
  fadeInUp,
  formatCountdown,
  formatDate,
  formatNaira,
  formatNumber,
  formatPercent,
  formatTicketNumber,
  formatTime,
  hoverSpring,
  isDesktopViewport,
  isMobileViewport,
  isTouchDevice,
  padZero,
  staggerChildren,
  triggerHaptic,
  truncate,
  useCountdown,
  useHaptic,
  useHoverPosition,
  useInView,
  useLocalStorage,
  useMousePosition,
  useScrollPosition,
  useSlideToConfirm,
  useToast,
  useViewport,
  vaultLockSpring,
  vaultSpring
};
